/* ═══════════════════════════════════════════════════════════════════════════════
   COMPANY QUOTA DEFERRED REVENUE ENGINE
   Computes ARR growth → ACV → GAAP recognition, deferred revenue balance,
   and cash received from company-level quota settings.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { computeRenewalLookback } from './drRecognition';
import { computeAEQuotaDR } from './computeAEQuotaDR';

const r2 = n => Math.round(n * 100) / 100;

/**
 * @param {Object} settings        — company_quota_settings row from DB
 * @param {string[]} forecastMonths — 12 month strings ["2026-01"..."2026-12"]
 * @param {string} closeMonth      — last actuals month e.g. "2025-12"
 * @returns {{ currentARR, newACV, grossRenewals, churn, netRenewals, totalACV, gaapRevenue, deferredBalance, cashReceived, schedule }}
 */
function computeCompanyQuotaDR(settings, forecastMonths, closeMonth) {
  settings = settings || {};
  const startMode = settings.start_mode || 'zero';
  const renewalMode = settings.renewal_mode || 'auto';
  const churnRate = parseFloat(settings.churn_rate) || 0;
  const manualRenewals = settings.manual_renewals || {};
  /* ⓘ B6 COMMIT B — `opening_bs_balance` and `existing_monthly_rev` are no longer read
     HERE. They are still read, from this same `settings` object, by the AE engine this
     module now delegates recognition and the balance roll to. Removing the dead locals
     rather than leaving them is deliberate: an unused destructure of a live column reads
     like a dropped input to the next person, and it moved the lint baseline. */
  const existingTotalACV = settings.existing_total_acv || {};
  const openingARR = parseFloat(settings.opening_arr) || 0;
  const growthType = settings.growth_type || 'flat';
  const growthMode = settings.growth_mode || 'compounding';
  const growthInputs = settings.growth_inputs || {};

  /* ★★ THE SPAN IS NOW THE CALLER'S WINDOW — full note in computeAEQuotaDR.js. This used to
     be a fixed 24-month list bounded by the LITERAL 24, so a window longer than 24 months
     found NO KEY in the emit loop's `x[m] || 0` and rendered $0.00 from month 25. B6's defect
     at a different number. ⛔ Not replaced by a larger literal. */

  const currentARR = {};
  const newACV = {};
  const grossRenewals = {};
  const churn = {};
  const netRenewals = {};
  const totalACV = {};
  const gaapRevenue = {};
  const deferredBalance = {};
  const cashReceived = {};
  const schedule = [];
  const forecastSold = {};   // Q-B2: the forecast-sold track, kept exactly as the AE engine keeps it

  forecastMonths.forEach(m => {
    currentARR[m] = 0;
    newACV[m] = 0;
    grossRenewals[m] = 0;
    churn[m] = 0;
    netRenewals[m] = 0;
    totalACV[m] = 0;
    gaapRevenue[m] = 0;
    deferredBalance[m] = 0;
    cashReceived[m] = 0;
  });

  // net_arr: new ACV grows off the prior month's NET ARR balance (prior + new − churn)
  // — bookings-based; ≡ prior-revenue growth ≤12mo, intentionally divergent >12mo.
  // churn[m] from computeRenewalLookback (single source). This branch FUSES Step 1 + Step 2
  // into one forward loop (the netARR roll needs churn[m], a Step-2 output); Step 2 below is
  // skipped for net_arr so totalACV is not double-written. currentARR stays GROSS (display
  // semantics unchanged); netARR is internal-only (drives newACV, never returned).
  const isNetArr = growthType === 'pct' && growthMode === 'net_arr';

  // ── Step 1: Compute ARR and newACV ──
  if (isNetArr) {
    const netARR = {};
    let prevNet = openingARR, prevGross = openingARR;
    forecastMonths.forEach(m => {
      if (m <= closeMonth) return;
      const pct = parseFloat(growthInputs[m]) || 0;
      newACV[m] = Math.max(0, r2(prevNet * pct / 100));        // grow off prior NET ARR
      currentARR[m] = r2(prevGross + newACV[m]);               // GROSS display (unchanged semantics)
      const r = computeRenewalLookback({ month: m, renewalMode, manualRenewals, existingTotalACV, totalACV, churnRate, newACV, closeMonth, startMode, forecastSold });
      grossRenewals[m] = r.grossRenewals;
      churn[m] = r.churn;
      netRenewals[m] = r.netRenewals;
      totalACV[m] = r.totalACV;
      forecastSold[m] = r.forecastSoldValue;
      netARR[m] = r2(prevNet + newACV[m] - churn[m]);          // net roll drives next newACV
      prevNet = netARR[m];
      prevGross = currentARR[m];
    });
  } else {
    let priorARR = openingARR;
    forecastMonths.forEach(m => {
      if (m <= closeMonth) return;

      let growthAmount = 0;
      if (growthType === 'flat') {
        growthAmount = r2(parseFloat(growthInputs[m]) || 0);
      } else if (growthType === 'pct') {
        const pct = parseFloat(growthInputs[m]) || 0;
        if (growthMode === 'compounding') {
          growthAmount = r2(priorARR * pct / 100);
        } else {
          growthAmount = r2(openingARR * pct / 100);
        }
      }

      currentARR[m] = r2(priorARR + growthAmount);
      newACV[m] = Math.max(0, r2(currentARR[m] - priorARR));
      priorARR = currentARR[m];
    });
  }

  // ── Step 2: Build totalACV (newACV + renewals - churn) ──
  // Shared renewal-lookback primitive. The caller writes totalACV[m] back into its map
  // AFTER the call so the next iteration's auto lookback (totalACV[m-12]) chain holds.
  // Skipped for net_arr (the fused Step-1 loop above already produced Step-2's outputs).
  if (!isNetArr) {
    forecastMonths.forEach(m => {
      if (m <= closeMonth) return;
      const r = computeRenewalLookback({ month: m, renewalMode, manualRenewals, existingTotalACV, totalACV, churnRate, newACV, closeMonth, startMode, forecastSold });
      grossRenewals[m] = r.grossRenewals;
      churn[m] = r.churn;
      netRenewals[m] = r.netRenewals;
      totalACV[m] = r.totalACV;
      forecastSold[m] = r.forecastSoldValue;
    });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ★★ B6 COMMIT B — RECOGNITION, THE DEFERRED BALANCE AND CASH ARE THE AE ENGINE.
  //
  // The two operator templates (Def_Rev_template_annual_ver4 for AE and its CQ sibling)
  // are FORMULA-IDENTICAL on rows 9-22 — renewals, churn, newACV, totalACV, the three
  // recognition streams, revenue and the balance roll. The ONLY differences are UPSTREAM
  // INPUTS: AE takes new ACV as a monthly input, CQ derives it from growth% × prior ARR
  // and additionally rolls ARR forward. ⇒ THE DEFERRED-REVENUE MECHANICS ARE ONE ENGINE,
  // and this module keeps only the half that is genuinely its own (Steps 1-2 above).
  //
  // ⛔ WHY CQ'S OWN RECOGNITION WAS WRONG BY CONSTRUCTION — THE TAILS PRINCIPLE.
  //   Billing systems recognize on a days-of-YEAR basis; the forecast uses 12ths for
  //   tractability. A $1,200 contract closing on the 30th books $100 of ARR and $3.29 of
  //   GAAP revenue. ⇒ **ARR ≠ GAAP × 12, BY DESIGN.** Close-month ARR and close-month GAAP
  //   revenue are two INDEPENDENT billing-system numbers, neither derivable from the other.
  //   CQ passed `conserveTotal: openingARR` alongside `closeRunRate` = the close-month GAAP
  //   revenue, forcing twelve months of GAAP to sum to an ARR level. Measured:
  //   12 × 91,491.83 = 1,097,901.96 ≠ 997,401.48. The twelfth slot is computed as
  //   `conserveTotal − everything already allocated`, so it went to −9,008.65 and every
  //   month after it collapsed.
  //   ★ IT PRODUCED A ZERO REMAINDER ONLY WHILE opening_arr WAS FUDGED TO 1,097,901.96
  //     (= 12 × the run-rate). KPI-6a restored the TRUE billing ARR of 997,401.48 and the
  //     remainder went negative — SILENTLY, because no tenant forecasts past month 9.
  //   ⇒ ANY BRANCH THAT FORCES 12 MONTHS OF GAAP TO SUM TO AN ARR LEVEL IS WRONG BY
  //     CONSTRUCTION. That is the root. The number 12 is not.
  //
  // ★ `opening_arr` KEEPS ITS TWO LEGITIMATE USES AND LOSES ONLY THE THIRD:
  //     1. the ARR growth base (Step 1 above) ................................ KEPT
  //     2. `conserveTotal` for RECOGNITION .................. ⛔ REMOVED (the defect)
  //     3. the KPI ARR seed in runFullModel — a FALLBACK only, since
  //        existing_arr[close] wins and is populated on all four tenants ...... KEPT
  //
  // ⓘ THE ENGINE'S OWN WARNING IS SATISFIED, NOT IGNORED. computeAEQuotaDR Step 3b(3)
  //   said "do NOT unify with CQ's cohort form without a bookShape param". CQ is not
  //   carrying a cohort form across — it is MOVING TO the perpetual shape, so there is
  //   one shape and no param is needed. That comment is updated at its source.
  //
  // ⛔ THE BOOKINGS SIDE IS NOT DELEGATED AND DOES NOT MOVE. Steps 1-2 above still own
  //   newACV, the ARR roll-forward, renewals, churn and totalACV. AE recomputes renewals
  //   and churn from the SAME shared `computeRenewalLookback` with the same settings, so
  //   they reproduce identically — asserted at 0 of 90 cells moved.
  // ══════════════════════════════════════════════════════════════════════════════
  const aeShaped = computeAEQuotaDR(newACV, settings, forecastMonths, closeMonth);
  forecastMonths.forEach(m => {
    gaapRevenue[m] = aeShaped.gaapRevenue[m] || 0;
    deferredBalance[m] = aeShaped.deferredBalance[m] || 0;
    cashReceived[m] = aeShaped.cashReceived[m] || 0;
  });
  (aeShaped.schedule || []).forEach(c => schedule.push(c));

  // ── Build output: only forecastMonths ──
  const out = {
    currentARR: {},
    newACV: {},
    grossRenewals: {},
    churn: {},
    netRenewals: {},
    totalACV: {},
    gaapRevenue: {},
    deferredBalance: {},
    cashReceived: {},
    schedule,
  };

  forecastMonths.forEach(m => {
    out.currentARR[m] = currentARR[m] || 0;
    out.newACV[m] = newACV[m] || 0;
    out.grossRenewals[m] = grossRenewals[m] || 0;
    out.churn[m] = r2(-(churn[m] || 0));
    out.netRenewals[m] = netRenewals[m] || 0;
    out.totalACV[m] = totalACV[m] || 0;
    out.gaapRevenue[m] = gaapRevenue[m] || 0;
    out.deferredBalance[m] = deferredBalance[m] || 0;
    out.cashReceived[m] = cashReceived[m] || 0;
  });

  return out;
}

export { computeCompanyQuotaDR, r2 };
